import { pluralize } from "./inflection";

export function uniqueMembers<
  T extends { user?: { id: string; deactivated?: boolean | null } | null },
>(grants: readonly T[]): NonNullable<T["user"]>[] {
  const seen = new Set<string>();
  const members: NonNullable<T["user"]>[] = [];
  for (const g of grants) {
    if (!g.user || g.user.deactivated || seen.has(g.user.id)) continue;
    seen.add(g.user.id);
    members.push(g.user as NonNullable<T["user"]>);
  }
  return members;
}

export function uniqueMemberCount(
  grants: readonly {
    user?: { id: string; deactivated?: boolean | null } | null;
  }[]
): number {
  return uniqueMembers(grants).length;
}

export function memberCountLabel(count: number): string {
  return `${count} ${pluralize("member", count)}`;
}

export type MemberGroupMap = Map<string, string[]>;

type GroupWithGrants = {
  name: string;
  roleGrantsAsScope: readonly { userId: string | null }[];
};

export function buildMemberGroupMap(
  groups: readonly GroupWithGrants[]
): MemberGroupMap {
  const map = new Map<string, string[]>();
  for (const group of groups) {
    for (const grant of group.roleGrantsAsScope) {
      if (!grant.userId) continue;
      let names = map.get(grant.userId);
      if (!names) {
        names = [];
        map.set(grant.userId, names);
      }
      if (!names.includes(group.name)) names.push(group.name);
    }
  }
  for (const names of map.values()) names.sort((a, b) => a.localeCompare(b));
  return map;
}

export function userGroupNamesForMember(
  memberId: string,
  memberGroupMap: MemberGroupMap
): string[] {
  return memberGroupMap.get(memberId) ?? [];
}
